/**
 * The two menus on the composer: the model pill on the right, and the "+"
 * on the left — photos, a repository, skills, people. The shape is the one the
 * desktop chat apps taught. Nothing here asks for an agent or a computer;
 * the server derives those from these picks (server/agents.ts).
 */
import { useCallback, useEffect, useState } from "react";
import { shortName } from "../../shared/author";
import { groupByProvider, modelBlurb, modelLabel, providerLabel } from "../../shared/models";
import { baseBranch, type ProjectDto } from "../../shared/projects";
import type { ChatSettings } from "../../shared/settings";
import { SKILLS, skillNames } from "../../shared/skills";
import { api, type GitHubInfo, type GitHubRepo, type MenuDto } from "../lib/api";
import { beginGitHubConnect } from "../lib/github";
import { describeError } from "../lib/errors";
import { useSession } from "../store";
import { MenuBack, MenuHeading, MenuItem, Popover } from "./Menu";

export function ModelPill({ settings, menu, onChange }: { settings: ChatSettings; menu: MenuDto | null; onChange: (s: ChatSettings) => void }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const models = menu?.models ?? [];
  const groups = groupByProvider(models.includes(settings.model) ? models : [settings.model, ...models]);

  return (
    <div className="pill-wrap">
      <button type="button" className={`pill${open ? " on" : ""}`} onClick={() => (open ? close() : setOpen(true))} aria-haspopup="menu" aria-expanded={open}>
        <span className="pill-main">{modelLabel(settings.model)}</span>
        <span className="pill-caret">⌄</span>
      </button>
      <Popover open={open} onClose={close} align="right" className="model-menu">
        {!menu && <MenuHeading>Loading…</MenuHeading>}
        {groups.map((g) => (
          <div key={g.provider}>
            <MenuHeading>{providerLabel(g.provider)}</MenuHeading>
            {g.models.map((m) => (
              <MenuItem
                key={m}
                label={modelLabel(m)}
                detail={modelBlurb(m) ?? undefined}
                checked={m === settings.model}
                onClick={() => {
                  onChange({ ...settings, model: m });
                  close();
                }}
              />
            ))}
          </div>
        ))}
      </Popover>
    </div>
  );
}

export interface Extras {
  invitees: string[];
}

export function AddMenu({
  settings,
  menuError,
  onChange,
  extras,
  onExtras,
  onAttach,
  projects,
  onProjectsChanged,
}: {
  settings: ChatSettings;
  menuError: string | null;
  onChange: (s: ChatSettings) => void;
  extras: Extras;
  onExtras: (e: Extras) => void;
  onAttach: () => void;
  projects: ProjectDto[];
  onProjectsChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"root" | "skills" | "people" | "projects" | "new-project">("root");
  const [email, setEmail] = useState("");
  const close = useCallback(() => {
    setOpen(false);
    setView("root");
  }, []);
  const project = settings.projectId ? (projects.find((p) => p.id === settings.projectId) ?? null) : null;

  const toggleSkill = (id: string) => {
    const skills = settings.skills.includes(id) ? settings.skills.filter((s) => s !== id) : [...settings.skills, id].sort();
    onChange({ ...settings, skills });
  };
  const chosenSkills = skillNames(settings.skills);

  const addEmail = () => {
    const e = email.trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    if (!extras.invitees.includes(e)) onExtras({ invitees: [...extras.invitees, e] });
    setEmail("");
  };

  return (
    <div className="pill-wrap">
      <button type="button" className={`icon plus${open ? " on" : ""}`} onClick={() => (open ? close() : setOpen(true))} aria-label="Add" aria-haspopup="menu" aria-expanded={open}>
        +
      </button>
      <Popover open={open} onClose={close} className="add-menu">
        {view === "root" && (
          <>
            <MenuItem
              icon="📎"
              label="Add photos"
              detail="Or paste or drop them in"
              onClick={() => {
                close();
                onAttach();
              }}
            />
            <div className="menu-sep" />
            <MenuItem label="Repository" detail={project ? project.name : "Open this session in a GitHub repository"} arrow onClick={() => setView("projects")} />
            <MenuItem label="Skills" detail={chosenSkills.length ? chosenSkills.join(", ") : "PDFs, spreadsheets, slides and more"} arrow onClick={() => setView("skills")} />
            <div className="menu-sep" />
            <MenuItem label="People" detail={extras.invitees.length ? `${extras.invitees.length} invited` : "Invite by email; they see it when they sign in"} arrow onClick={() => setView("people")} />
            {menuError && <div className="menu-problem">{menuError}</div>}
          </>
        )}
        {view === "projects" && (
          <>
            <MenuBack onClick={() => setView("root")} />
            {projects.length === 0 && <MenuHeading>No projects yet. Add a repository and the chat's computer starts with a checkout of it.</MenuHeading>}
            {projects.map((p) => (
              <MenuItem
                key={p.id}
                label={p.name}
                detail={`${p.repoUrl.replace(/^https:\/\//, "")} · ${p.base}${p.role === "member" ? ` · ${shortName(p.ownerEmail)}'s` : ""}`}
                checked={settings.projectId === p.id}
                onClick={() => onChange({ ...settings, projectId: settings.projectId === p.id ? null : p.id })}
              />
            ))}
            <div className="menu-sep" />
            <MenuItem label="Open another repository…" detail="Choose from the Salon GitHub App" arrow onClick={() => setView("new-project")} />
            {projects.length > 0 && <MenuHeading>Everyone in a project is in every chat started in it, and the project's owner pays for them.</MenuHeading>}
          </>
        )}
        {view === "new-project" && (
          <>
            <MenuBack onClick={() => setView("projects")} />
            <NewProject
              onMade={(p) => {
                onProjectsChanged();
                onChange({ ...settings, projectId: p.id });
                setView("projects");
              }}
            />
          </>
        )}
        {view === "skills" && (
          <>
            <MenuBack onClick={() => setView("root")} />
            {SKILLS.map((s) => (
              <MenuItem key={s.id} toggle label={s.name} detail={s.blurb} checked={settings.skills.includes(s.id)} onClick={() => toggleSkill(s.id)} />
            ))}
            <MenuHeading>Each one teaches the chat how to work with that kind of file.</MenuHeading>
          </>
        )}
        {view === "people" && (
          <>
            <MenuBack onClick={() => setView("root")} />
            <form
              className="menu-form"
              onSubmit={(e) => {
                e.preventDefault();
                addEmail();
              }}
            >
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="someone@example.com" autoFocus />
              <button type="submit" className="small" disabled={!email.trim()}>
                Invite
              </button>
            </form>
            {extras.invitees.map((e) => (
              <MenuItem key={e} label={e} detail="Remove" onClick={() => onExtras({ invitees: extras.invitees.filter((x) => x !== e) })} />
            ))}
            <MenuHeading>They sign in with Fountain and find the chat waiting. You can also share a link once it starts.</MenuHeading>
          </>
        )}
      </Popover>
    </div>
  );
}

/**
 * The form behind "Open another repository…". GitHub lists only repositories
 * both this person and the Salon App installation can reach; no repository
 * token is typed into or retained by the browser.
 */
export function NewProject({ onMade }: { onMade: (p: ProjectDto) => void }) {
  const { toast } = useSession();
  const [info, setInfo] = useState<GitHubInfo | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[] | null>(null);
  const [slug, setSlug] = useState("");
  const [base, setBase] = useState("");
  const [setup, setSetup] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const selected = repos?.find((r) => r.slug === slug) ?? null;
  const branch = baseBranch(base);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.github();
      setInfo(next);
      if (next.connected) {
        const list = await api.githubRepos();
        setRepos(list);
        if (!slug && list[0]) {
          setSlug(list[0].slug);
          setBase(list[0].defaultBranch);
        }
      } else {
        setRepos(null);
      }
    } catch (err) {
      toast(describeError(err), "error");
    } finally {
      setLoading(false);
    }
  }, [slug, toast]);

  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const problem = base.trim() && !branch ? "That is not a branch name." : selected?.archived ? "That repository is archived." : null;

  const submit = async () => {
    if (!selected || !branch || busy) return;
    setBusy(true);
    try {
      const made = await api.createProject({ githubRepo: selected.slug, base: branch, setup: setup.trim() || undefined });
      toast(`${made.name} is ready. This session will work in its checkout.`);
      onMade(made);
    } catch (err) {
      toast(describeError(err), "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading && !info) return <MenuHeading>Checking GitHub…</MenuHeading>;
  if (!info?.configured) return <MenuHeading>This Salon does not have its GitHub App configured yet.</MenuHeading>;
  if (!info.connected) {
    return (
      <div className="menu-form project-form">
        <MenuHeading>Connect GitHub to choose from repositories where the Salon App is installed. Salon never asks you to paste a repository token.</MenuHeading>
        <button type="button" className="small" onClick={() => beginGitHubConnect(info.clientId!)}>
          Connect GitHub
        </button>
      </div>
    );
  }
  if (repos?.length === 0) {
    return (
      <div className="menu-form project-form">
        <MenuHeading>GitHub is connected as {info.login}, but the Salon App cannot reach a repository you can push to yet.</MenuHeading>
        {info.installUrl && (
          <a className="small button-link" href={info.installUrl} target="_blank" rel="noreferrer">
            Install or configure the App
          </a>
        )}
        <button type="button" className="small" onClick={() => void load()} disabled={loading}>
          {loading ? "Checking…" : "Refresh repositories"}
        </button>
      </div>
    );
  }

  return (
    <form
      className="menu-form project-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label>
        <span>Repository</span>
        <select
          value={slug}
          onChange={(e) => {
            const next = repos?.find((r) => r.slug === e.target.value);
            setSlug(e.target.value);
            if (next) setBase(next.defaultBranch);
          }}
          autoFocus
        >
          {repos?.map((repo) => (
            <option key={repo.slug} value={repo.slug} disabled={repo.archived}>
              {repo.slug}{repo.private ? " · private" : ""}{repo.archived ? " · archived" : ""}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Branch to start from</span>
        <input value={base} onChange={(e) => setBase(e.target.value)} placeholder="main" spellCheck={false} />
      </label>
      <label>
        <span>Setup command</span>
        <input value={setup} onChange={(e) => setSetup(e.target.value)} placeholder="npm install (optional, runs in the checkout)" spellCheck={false} />
      </label>
      {problem && <div className="menu-problem">{problem}</div>}
      <button type="submit" className="small" disabled={!selected || !branch || !!problem || busy}>
        {busy ? "Opening…" : "Use this repository"}
      </button>
      <MenuHeading>Salon gives the session a short-lived credential for this repository only, and refreshes it when the session starts.</MenuHeading>
    </form>
  );
}

/** What the "+" has added, as chips on the composer. */
export function Chips({ settings, extras, onChange, onExtras, projects }: { settings: ChatSettings; extras: Extras; onChange: (s: ChatSettings) => void; onExtras: (e: Extras) => void; projects: ProjectDto[] }) {
  const chips: { key: string; label: string; clear: () => void }[] = [];
  const project = settings.projectId ? projects.find((p) => p.id === settings.projectId) : null;
  if (project) chips.push({ key: `proj:${project.id}`, label: `⌥ ${project.name}`, clear: () => onChange({ ...settings, projectId: null }) });
  for (const s of SKILLS) if (settings.skills.includes(s.id)) chips.push({ key: `s:${s.id}`, label: s.name, clear: () => onChange({ ...settings, skills: settings.skills.filter((x) => x !== s.id) }) });
  for (const e of extras.invitees) chips.push({ key: `p:${e}`, label: `@ ${e}`, clear: () => onExtras({ invitees: extras.invitees.filter((x) => x !== e) }) });
  if (chips.length === 0) return null;
  return (
    <div className="chips">
      {chips.map((c) => (
        <span key={c.key} className="chip">
          {c.label}
          <button type="button" className="chip-x" onClick={c.clear} aria-label={`Remove ${c.label}`}>
            ×
          </button>
        </span>
      ))}
    </div>
  );
}
